import React, { useState } from 'react';
import { Edit, Trash2, Plus, Search, Package, Users, ShoppingCart, DollarSign } from 'lucide-react';
import PageHeader from './PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Input } from '../ui/input';
import SalesChart from './SalesChart';
import './Dashboard.css';

/**
 * Modern Design System Example
 * This component demonstrates all the new design components
 */
const ModernDesignExample = () => {
    const [searchTerm, setSearchTerm] = useState('');

    // Example data
    const sampleData = [
        { id: 1, name: 'Product A', status: 'active', price: '₹1,200', stock: 45 },
        { id: 2, name: 'Product B', status: 'inactive', price: '₹850', stock: 0 },
        { id: 3, name: 'Product C', status: 'pending', price: '₹2,500', stock: 12 },
    ];

    // Helper function to get badge variant
    const getStatusVariant = (status) => {
        const variantMap = {
            'active': 'default',
            'inactive': 'secondary',
            'pending': 'outline'
        };
        return variantMap[status] || 'default';
    };

    return (
        <div className="dashboard">
            {/* Page Header */}
            <PageHeader
                title="Modern Design System"
                subtitle="Example of all design components"
                actions={
                    <>
                        <Button variant="outline">
                            <Search size={20} style={{ marginRight: '8px' }} />
                            Search
                        </Button>
                        <Button variant="default">
                            <Plus size={20} style={{ marginRight: '8px' }} />
                            Add New
                        </Button>
                    </>
                }
            />

            {/* Sales Chart */}
            <SalesChart title="Sales Overview" />

            {/* Stats Cards */}
            <div className="dashboard__stats">
                <div className="stat-card stat-card--primary">
                    <div className="stat-card__header">
                        <div className="stat-card__icon">
                            <Package size={24} />
                        </div>
                    </div>
                    <div className="stat-card__value">1,234</div>
                    <div className="stat-card__label">Total Products</div>
                </div>

                <div className="stat-card stat-card--success">
                    <div className="stat-card__header">
                        <div className="stat-card__icon">
                            <Users size={24} />
                        </div>
                    </div>
                    <div className="stat-card__value">567</div>
                    <div className="stat-card__label">Active Users</div>
                </div>

                <div className="stat-card stat-card--warning">
                    <div className="stat-card__header">
                        <div className="stat-card__icon">
                            <ShoppingCart size={24} />
                        </div>
                    </div>
                    <div className="stat-card__value">89</div>
                    <div className="stat-card__label">Pending Orders</div>
                </div>

                <div className="stat-card stat-card--gold">
                    <div className="stat-card__header">
                        <div className="stat-card__icon">
                            <DollarSign size={24} />
                        </div>
                    </div>
                    <div className="stat-card__value">₹45,678</div>
                    <div className="stat-card__label">Total Revenue</div>
                </div>
            </div>

            {/* Buttons Example */}
            <Card>
                <CardHeader>
                    <CardTitle>Button Variants</CardTitle>
                </CardHeader>
                <CardContent>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <Button variant="default">Default Button</Button>
                        <Button variant="secondary">Secondary Button</Button>
                        <Button variant="destructive">Destructive Button</Button>
                        <Button variant="outline">Outline Button</Button>
                        <Button variant="ghost">Ghost Button</Button>
                        <Button variant="default" size="sm">Small Button</Button>
                        <Button variant="default" size="lg">Large Button</Button>
                        <Button variant="default" disabled>Disabled Button</Button>
                    </div>
                </CardContent>
            </Card>

            {/* Badges Example */}
            <Card>
                <CardHeader>
                    <CardTitle>Badge Variants</CardTitle>
                </CardHeader>
                <CardContent>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <Badge variant="default">Default</Badge>
                        <Badge variant="secondary">Secondary</Badge>
                        <Badge variant="destructive">Destructive</Badge>
                        <Badge variant="outline">Outline</Badge>
                    </div>
                </CardContent>
            </Card>

            {/* Form Inputs Example */}
            <Card>
                <CardHeader>
                    <CardTitle>Form Inputs</CardTitle>
                </CardHeader>
                <CardContent>
                    <div style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                                Product Name
                            </label>
                            <Input
                                name="productName"
                                placeholder="Enter product name"
                                required
                            />
                            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                This will be displayed to customers
                            </p>
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                                Price
                            </label>
                            <Input
                                name="price"
                                type="number"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Table Example */}
            <Card>
                <CardHeader>
                    <CardTitle>Data Table</CardTitle>
                    <div style={{ marginLeft: 'auto' }}>
                        <Input
                            name="search"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '300px' }}
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Price</TableHead>
                                <TableHead className="text-center">Stock</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sampleData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center">
                                        No products found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sampleData.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>
                                            <Badge variant={getStatusVariant(row.status)}>
                                                {row.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{row.price}</TableCell>
                                        <TableCell className="text-center">{row.stock}</TableCell>
                                        <TableCell className="text-right">
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <Button variant="outline" size="icon">
                                                    <Edit size={18} />
                                                </Button>
                                                <Button variant="destructive" size="icon">
                                                    <Trash2 size={18} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Color Palette Reference */}
            <Card>
                <CardHeader>
                    <CardTitle>Color Palette</CardTitle>
                </CardHeader>
                <CardContent>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                        <div>
                            <div style={{ width: '100%', height: '80px', backgroundColor: '#0A0A0A', borderRadius: '8px', marginBottom: '8px' }}></div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Black</div>
                            <div style={{ fontSize: '12px', color: '#4A4A4A' }}>#0A0A0A</div>
                        </div>
                        <div>
                            <div style={{ width: '100%', height: '80px', backgroundColor: '#FFFFFF', border: '1px solid #E8E8E8', borderRadius: '8px', marginBottom: '8px' }}></div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>White</div>
                            <div style={{ fontSize: '12px', color: '#4A4A4A' }}>#FFFFFF</div>
                        </div>
                        <div>
                            <div style={{ width: '100%', height: '80px', backgroundColor: '#FFD700', borderRadius: '8px', marginBottom: '8px' }}></div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Accent Gold</div>
                            <div style={{ fontSize: '12px', color: '#4A4A4A' }}>#FFD700</div>
                        </div>
                        <div>
                            <div style={{ width: '100%', height: '80px', backgroundColor: '#2196F3', borderRadius: '8px', marginBottom: '8px' }}></div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Accent Blue</div>
                            <div style={{ fontSize: '12px', color: '#4A4A4A' }}>#2196F3</div>
                        </div>
                        <div>
                            <div style={{ width: '100%', height: '80px', backgroundColor: '#4ADE80', borderRadius: '8px', marginBottom: '8px' }}></div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Active</div>
                            <div style={{ fontSize: '12px', color: '#4A4A4A' }}>#4ADE80</div>
                        </div>
                        <div>
                            <div style={{ width: '100%', height: '80px', backgroundColor: '#FF6B6B', borderRadius: '8px', marginBottom: '8px' }}></div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Inactive</div>
                            <div style={{ fontSize: '12px', color: '#4A4A4A' }}>#FF6B6B</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ModernDesignExample;
